import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal } from "../lib/anim";

const Counter = ({ to, suffix = "", duration = 1.6 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const startTime = performance.now();
    const step = (t) => {
      const p = Math.min((t - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, to, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
};

const STATS = [
  { label: "reduction in diarrheal illness through handwashing education (CDC)", value: 40, suffix: "%", pre: "up to" },
  { label: "reduction in respiratory illness with better hygiene (CDC)", value: 21, suffix: "%", pre: "up to" },
  { label: "essential personal-care items in every hygiene kit", value: 10, suffix: "+", pre: "" },
  { label: "hygiene kits we aim to distribute this summer", value: 200, suffix: "", pre: "our goal" },
];

export const Impact = () => (
  <section id="impact" data-testid="impact" className="py-24 md:py-32 bg-brand-ink text-white relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-brand-sky font-bold mb-4">Community Health Impact</p>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl leading-[1.02]">
          Prevention is the most cost-efficient medicine there is.
        </h2>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="border-t border-white/15 pt-6 h-full">
              {s.pre && <div className="text-sm text-white/40 mb-1">{s.pre}</div>}
              <div className="font-display text-5xl md:text-6xl font-semibold text-brand-green">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-4 text-white/60 leading-relaxed text-sm">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
    <div className="absolute -bottom-40 right-0 w-[40rem] h-[40rem] rounded-full bg-brand-blue/20 blur-3xl" />
  </section>
);
